import { isEmpty } from "lodash";
import { ComponentPropsWithoutRef, useMemo } from "react";
import { Control, FieldPath, FieldValues, RegisterOptions } from "react-hook-form";
import { cn } from "../../../../lib/utils";
import { getHarvestYearValidationMessage } from "../../../../utils/utils";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "../../../ui/common/form";
import { Input } from "../../../ui/common/input";

interface FormInputNumberProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> extends Omit<ComponentPropsWithoutRef<typeof Input>, "name" | "type" | "onChange"> {
  control: Control<TFieldValues>;
  name: TName;
  label: string;
  required?: boolean;
  description?: string;
  errorMessage?: string;
  // Number-specific options
  enableMinus?: boolean;
  removeDecimal?: boolean;
  enableDecimalWithMinus?: boolean;
  enableLimitedDecimal?: boolean;
  viewDecimalNumber?: number;
  isYearInput?: boolean;
  formatWithCommas?: boolean;
  // Right content display
  rightContentEnable?: boolean;
  rightContent?: string;
  // Custom onChange callback
  onChangeFunction?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  // Validation rules
  rules?: Omit<
    RegisterOptions<TFieldValues, TName>,
    "valueAsNumber" | "valueAsDate" | "setValueAs" | "disabled"
  >;
}

export function FormInputNumber<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
  control,
  name,
  label,
  required = false,
  description,
  errorMessage,
  enableMinus = false,
  removeDecimal = false,
  enableDecimalWithMinus = false,
  enableLimitedDecimal = false,
  viewDecimalNumber = 4,
  isYearInput = false,
  formatWithCommas: shouldFormatWithCommas = false,
  rightContentEnable = false,
  rightContent = "",
  onChangeFunction,
  rules,
  placeholder = "0",
  disabled = false,
  className,
  ...inputProps
}: FormInputNumberProps<TFieldValues, TName>) {
  // Format number with commas
  const formatWithCommas = (value: string) => {
    if (value === null || value === undefined) return "";
    const strValue = String(value);
    const isNegative = strValue.startsWith("-");
    const cleanValue = strValue.replace(/[^0-9.]/g, "");
    const [integerPart, decimalPart] = cleanValue.split(".");
    const int = parseInt(integerPart);
    const formattedInt = isNaN(int) ? "" : integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

    const formattedValue =
      decimalPart !== undefined ? `${formattedInt}.${decimalPart}` : formattedInt;

    return isNegative ? `-${formattedValue}` : formattedValue;
  };

  // Process input value
  const processInput = (value: any): string => {
    value = String(value ?? "");

    // Year input - only digits, max 4 characters
    if (isYearInput) {
      value = value.replace(/[^\d]/g, "");
      return value.slice(0, 4);
    }

    // Enable minus values (enableDecimalWithMinus implies enableMinus)
    const allowMinus = enableMinus || enableDecimalWithMinus;
    if (allowMinus) {
      value = enableDecimalWithMinus
        ? value.replace(/[^0-9.-]/g, "")
        : value.replace(/[^0-9-]/g, "");

      // Handle minus sign
      if (value.includes("-")) {
        const minusCount = (value.match(/-/g) || []).length;
        if (minusCount > 1) {
          value = value.replace(/-/g, (match: any, offset: any) => (offset === 0 ? "-" : ""));
        }
        if (value.indexOf("-") > 0) {
          value = value.replace("-", "");
          value = "-" + value;
        }
      }

      // Handle decimal with minus
      if (enableDecimalWithMinus) {
        const dotCount = (value.match(/\./g) || []).length;
        if (dotCount > 1) {
          const firstDotIndex = value.indexOf(".");
          value = value.replace(/\./g, (match: any, offset: any) =>
            offset === firstDotIndex ? "." : ""
          );
        }
      }
      return value;
    }

    // Regular number input
    value = removeDecimal ? value.replace(/[^0-9]/g, "") : value.replace(/[^0-9.]/g, "");

    // Allow only one decimal point
    if (!removeDecimal) {
      const dotCount = (value.match(/\./g) || []).length;
      if (dotCount > 1) {
        const firstDotIndex = value.indexOf(".");
        value = value.replace(/\./g, (match: any, offset: any) =>
          offset === firstDotIndex ? "." : ""
        );
      }
    }

    // Limit decimal places
    if (enableLimitedDecimal && !removeDecimal) {
      const [intPart, decimalPart] = value.split(".");
      if (decimalPart && decimalPart.length > viewDecimalNumber) {
        value = `${intPart}.${decimalPart.substring(0, viewDecimalNumber)}`;
      }
    }

    return value;
  };

  // Process output value (raw value for form state)
  const processOutput = (value: string): any => {
    if (!value) return "";

    // For minus mode (including enableDecimalWithMinus), return as-is
    const allowMinus = enableMinus || enableDecimalWithMinus;
    if (allowMinus) {
      return value;
    }

    const cleanedNumber = value.replace(/[^0-9.]/g, "");
    return isNaN(parseFloat(cleanedNumber)) ? "" : cleanedNumber;
  };

  // Build validation rules
  const validationRules = useMemo(() => {
    const baseRules: any = {
      required: required ? errorMessage || "This field is required" : false,
      ...rules
    };

    // Add year validation whenever this is a year input
    if (isYearInput) {
      baseRules.validate = {
        ...baseRules.validate,
        yearValid: (v: any) => {
          if (!required && (v === undefined || v === null || v === "")) {
            return true;
          }
          return getHarvestYearValidationMessage(v) ?? true;
        }
      };
    }

    return baseRules;
  }, [required, errorMessage, isYearInput, rules]);

  return (
    <FormField
      control={control}
      name={name}
      rules={validationRules}
      render={({ field }) => {
        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          const raw = e.target.value.replace(/,/g, "");
          const processedValue = processOutput(raw);
          field.onChange(processedValue);

          if (onChangeFunction) {
            onChangeFunction(e);
          }
        };

        const displayValue = (() => {
          const value = field.value ?? "";
          const rawValue = processInput(value);
          return isYearInput || !shouldFormatWithCommas ? rawValue : formatWithCommas(rawValue);
        })();

        return (
          <FormItem>
            <FormLabel>
              {label}
              {required && !isEmpty(label) && <span className="tw-text-destructive tw-ml-0.5">*</span>}
            </FormLabel>
            <div className="tw-relative">
              <FormControl>
                <Input
                  {...inputProps}
                  type="text"
                  value={displayValue}
                  onChange={handleChange}
                  onBlur={field.onBlur}
                  placeholder={placeholder}
                  disabled={disabled}
                  className={cn(rightContentEnable && "tw-pr-20", className)}
                />
              </FormControl>
              {rightContentEnable && rightContent && (
                <div className="tw-absolute tw-right-1 tw-top-1/2 tw--translate-y-1/2 tw-flex tw-items-center tw-h-10 tw-px-3 tw-bg-muted tw-rounded-md">
                  <span className="tw-text-xs tw-font-medium tw-text-muted-foreground">{rightContent}</span>
                </div>
              )}
            </div>
            {description && <p className="tw-text-sm tw-text-muted-foreground">{description}</p>}
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}
